/* E4N Minimal — effects.js
   Scroll reveal, soft parallax, shrinking/blurring header, page-load fade.
   Vanilla JS, no dependencies. Everything respects prefers-reduced-motion. */
(function () {
  'use strict';

  var root = document.documentElement;
  var body = document.body;
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  var reduced = reduce.matches;
  if (reduced) root.classList.add('reduced-motion');

  /* ---- Page-load fade ---- */
  function markLoaded() { root.classList.add('is-loaded'); }
  if (document.readyState === 'complete') markLoaded();
  else window.addEventListener('load', markLoaded, { once: true });
  // Safety net: never leave the page invisible.
  setTimeout(markLoaded, 1200);
  // Restore visibility when navigating back from bfcache.
  window.addEventListener('pageshow', function (e) { if (e.persisted) markLoaded(); });

  /* ---- Scroll reveal (IntersectionObserver) ---- */
  var revealEnabled = body.getAttribute('data-reveal') !== 'false' && !reduced && 'IntersectionObserver' in window;

  function revealAll(scope) {
    (scope || document).querySelectorAll('[data-reveal]').forEach(function (el) { el.classList.add('is-revealed'); });
  }

  var io = null;
  if (revealEnabled) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
  }

  function observe(scope) {
    var nodes = (scope || document).querySelectorAll('[data-reveal]:not(.is-revealed)');
    if (!revealEnabled) { nodes.forEach(function (el) { el.classList.add('is-revealed'); }); return; }
    nodes.forEach(function (el) {
      // Elements already in the viewport on load reveal immediately (avoids a blank first paint).
      var r = el.getBoundingClientRect();
      if (r.top < window.innerHeight * 0.92 && r.bottom > 0) el.classList.add('is-revealed');
      else io.observe(el);
    });
  }
  observe();
  window.e4nReveal = observe;

  // Content injected later (cart drawer, recommendations, theme editor) gets observed too.
  if ('MutationObserver' in window) {
    var mo = new MutationObserver(function (muts) {
      var needs = false;
      muts.forEach(function (m) { if (m.addedNodes.length) needs = true; });
      if (needs) observe();
    });
    mo.observe(body, { childList: true, subtree: true });
  }

  /* ---- Header: shrink + blur on scroll, hide on fast scroll down (mobile only) ---- */
  var header = document.querySelector('[data-header]');
  if (header) {
    var lastY = window.scrollY;
    var ticking = false;
    var sticky = header.getAttribute('data-sticky') === 'true';
    function onScroll() {
      var y = window.scrollY;
      header.classList.toggle('is-scrolled', y > 24);
      if (sticky && window.innerWidth < 900 && !body.classList.contains('drawer-open')) {
        if (y > lastY + 8 && y > 200) header.classList.add('is-hidden');
        else if (y < lastY - 4) header.classList.remove('is-hidden');
      } else {
        header.classList.remove('is-hidden');
      }
      lastY = y;
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(onScroll); ticking = true; }
    }, { passive: true });
    onScroll();
  }

  /* ---- Soft parallax on hero layers ---- */
  var parallaxEnabled = body.getAttribute('data-parallax') !== 'false' && !reduced && window.matchMedia('(hover: hover)').matches;
  var scenes = Array.prototype.slice.call(document.querySelectorAll('[data-parallax-scene]'));
  if (parallaxEnabled && scenes.length) {
    var pTicking = false;
    function parallax() {
      scenes.forEach(function (scene) {
        var rect = scene.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;
        var progress = -rect.top; // px scrolled past the top of the scene
        scene.querySelectorAll('[data-parallax-layer]').forEach(function (layer) {
          var f = parseFloat(layer.getAttribute('data-parallax-layer')) || 0;
          layer.style.transform = 'translate3d(0,' + (progress * f).toFixed(1) + 'px,0)';
        });
      });
      pTicking = false;
    }
    window.addEventListener('scroll', function () {
      if (!pTicking) { window.requestAnimationFrame(parallax); pTicking = true; }
    }, { passive: true });
    parallax();
  }

  /* ---- Ticker: duplicate groups until the track fills twice the viewport (keeps loop seamless) ---- */
  document.querySelectorAll('[data-ticker]').forEach(function (ticker) {
    var track = ticker.querySelector('.ticker__track');
    if (!track || reduced) return;
    var groups = track.children;
    if (groups.length < 2) return;
    var guard = 0;
    while (track.scrollWidth < ticker.clientWidth * 2 && guard < 6) {
      var clone = groups[0].cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      track.appendChild(clone);
      guard++;
    }
  });

  /* ---- Button ripple-free micro-interaction: magnetic nudge toward pointer (desktop) ---- */
  if (!reduced && window.matchMedia('(hover: hover)').matches) {
    document.addEventListener('pointermove', function (e) {
      var btn = e.target.closest && e.target.closest('.btn--primary, .btn--light');
      if (!btn) return;
      var r = btn.getBoundingClientRect();
      var dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      var dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      btn.style.setProperty('--mx', (dx * 4).toFixed(2) + 'px');
      btn.style.setProperty('--my', (dy * 4).toFixed(2) + 'px');
      btn.style.transform = 'translate(var(--mx), calc(var(--my) - 2px))';
    }, { passive: true });
    document.addEventListener('pointerout', function (e) {
      var btn = e.target.closest && e.target.closest('.btn--primary, .btn--light');
      if (btn) btn.style.transform = '';
    }, { passive: true });
  }

  /* ---- Theme editor: reveal everything inside a section that is being edited ---- */
  document.addEventListener('shopify:section:load', function (e) { revealAll(e.target); });
  document.addEventListener('shopify:section:select', function (e) { revealAll(e.target); });
  document.addEventListener('shopify:block:select', function (e) { revealAll(e.target.closest('.shopify-section') || e.target); });

  reduce.addEventListener && reduce.addEventListener('change', function (ev) { if (ev.matches) { root.classList.add('reduced-motion'); revealAll(); } });
})();
